import './FilterToggle.scss';
import { Select } from '@components/Component/Field/Select/Select.js';
import { ReplaySubject } from 'rxjs';
import { forEachKey } from '@components/Utility/index.js';
import { Application } from '@components/Component/Container/Application/Application.js';
import { debounceTime, takeUntil } from 'rxjs/operators';
import DIContainer from '@components/DependencyInjectionContainer.js';
/**
 * Class `FilterToggle` extends from the `Select` class.
 * It represents a select input field that allows a user to quickly toggle a set of filters to previously saved values.
 *
 * Note: FilterToggle only updates the record after a user change. On initial load, or when linking a record, it is expected that the filters would already have been loaded into the record.
 */
export class FilterToggle extends Select {
    get includeSaveButton() { return this._includeSaveButton; }
    set includeSaveButton(value) {
        this._propertySet('includeSaveButton', value);
        this.refreshButton('save');
    }
    get includeResetButton() { return this._includeResetButton; }
    set includeResetButton(value) {
        this._propertySet('includeResetButton', value);
        this.refreshButton('refresh');
    }
    get hasChanges() { return this._hasChanges; }
    set hasChanges(changes) {
        this._propertySet('hasChanges', changes);
        this.renderHasChanges();
    }
    renderHasChanges() {
        if (this.drawn) {
            // this.buttonActiveSet('save', this.hasChanges, false);
            this.renderValue();
            this.buttonDisabledSet('save', !this.hasChanges);
            this.buttonDisabledSet('refresh', !this.hasChanges);
        }
    }
    renderCustomSelectValue(value) {
        super.renderCustomSelectValue(value);
        if (this.displayBox && this.displayBoxText && this.hasChanges) {
            this.displayBoxText.textContent += '*';
        }
    }
    // private _excludeList:  Array<string> = [];
    get excludeList() { return this.record?.filterExcludeList || []; }
    // set excludeList(value:  Array<string>) {
    // 	if (this.record) {
    // 		this.record.filterExcludeList = value;
    // 	}
    // }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'FilterToggle';
        this.initiatorId = Math.random().toString(36).slice(2);
        this._includeSaveButton = true;
        this._includeResetButton = true;
        this._hasChanges = false;
        this.optionValueKey = 'value';
        this.optionDisplayKey = 'displaytext';
        this.endpoint = 'FilterDef';
        this.action = 'filterNameList';
        this.columnName = '_filterset';
        this.dataRequestOnInit = false;
        this.dataRequestOnRecordLoad = false;
        this.dataRequestOnParameterChange = false;
        this.allowFreeFormEntry = true;
        this.allowDeleteOptionData = true;
        this.cacheRequest = true;
    }
    initializeButtons() {
        this.iconSubject = new ReplaySubject(1);
        this.iconSubject.next('save');
        this.buttonAdd({
            icon: this.iconSubject,
            id: 'save',
            eventConfigName: 'saveFilters',
            includeProperty: 'includeSaveButton',
            defaultIncludePropertyValue: true,
            onClick: () => {
                this.saveFilters();
            }
        });
        this.buttonAdd({
            icon: 'settings_backup_restore',
            id: 'refresh',
            eventConfigName: '',
            includeProperty: 'includeResetButton',
            defaultIncludePropertyValue: true,
            onClick: () => {
                this.loadFilters();
            }
        });
    }
    draw() {
        super.draw();
        this.renderHasChanges();
        DIContainer.getInstance('BroadcastManager')
            .listen('refresh_filtermodule')
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ payload, initiatorId }) => {
            if (this.initiatorId !== initiatorId && payload.module === this.record?.filterModule) {
                if (payload.deletedFilterSet !== 'Default' && this.value === payload.deletedFilterSet) {
                    this.value = 'Default';
                    this.loadFilters();
                }
                this.refreshData();
            }
        });
    }
    defaultUserChangeEvent(newValue, oldValue, event, renderValueOnUserChange = false) {
        super.defaultUserChangeEvent(newValue, oldValue, event, renderValueOnUserChange);
        // now time to load the new filterset
        if (newValue !== oldValue) {
            if (event?.target?.classList.contains('freeform')) {
                this.saveFilters();
            }
            else {
                this.loadFilters();
            }
        }
    }
    setupRecordSubscription(record) {
        super.setupRecordSubscription(record);
        this.filterModuleSubscription = record.propertyGet$('filterModule')
            .subscribe((module) => {
            if (!module) {
                this.clearOptionData();
                this.staticParamAdd('module', '');
            }
            else {
                this.staticParamAdd('module', module);
                this.refreshData();
            }
        });
    }
    saveFilters() {
        console.log('save the filter set now', this.record);
        if (!this.record) {
            console.error('shouldnt happen, a filtertoggle is required to have a record');
            return;
        }
        this.buttonAnimate('save', 'spin');
        this.record.saveFilters().subscribe(() => {
            this.buttonAnimate('save', '');
            this.iconSubject?.next('check');
            setTimeout(() => { this.iconSubject?.next('save'); }, 2000);
            forEachKey(this.record.changedData, (val, key) => {
                if (this.excludeList.indexOf(key) === -1) {
                    delete this.record.changedData[key];
                }
            });
            this.refreshData({ refreshCache: true }).subscribe(() => {
                this.broadcastRefreshModule();
            });
        });
    }
    clearCache() {
        return this.record?.clearFilterCache();
    }
    loadFilters() {
        console.log('loading the filter set now');
        if (!this.record) {
            console.error('shouldnt happen, a filtertoggle is required to have a record');
            return;
        }
        this.handleProgress(0);
        this.record.loadFilters().subscribe({
            next: (oResp) => {
                this.handleProgress(100);
                this.trigger('loadFiltersResponse', {
                    defaultEventHandler: (oResp) => {
                    }
                }, oResp);
            },
            error: () => {
                this.handleProgress(100);
            }
        });
    }
    handleOptionDelete(optionElement) {
        const optionValue = optionElement.getAttribute('data-value') || '';
        this.clearCache()?.subscribe();
        if (optionValue !== 'Default') {
            super.handleOptionDelete(optionElement);
            this.deleteFilters(optionValue);
        }
        else {
            // TODO: captionkey
            Application.getInstance().confirm({ caption: 'Reset default filters to system default?', modalStyle: 'warning' })
                .subscribe(() => {
                this.record.defaultData = {};
                this.deleteFilters(optionValue);
            });
        }
    }
    deleteFilters(filterSetName) {
        this.trigger('deleteFilters', {
            defaultEventHandler: (filterSetName) => {
                console.log('deleting the filter set now');
                if (!this.record) {
                    console.error('shouldnt happen, a filtertoggle is required to have a record');
                    return;
                }
                const requestData = {
                    module: this.record.filterModule,
                    filterSetName: filterSetName
                };
                this.Request.DELETE({
                    endpoint: '/filterdef/',
                    action: 'delete',
                    loadingMessage: 'Loading...',
                    requestData: requestData,
                    initiator: this
                }).subscribe(() => {
                    if (filterSetName !== 'Default') {
                        if (!this.value) {
                            this.value = 'Default';
                            this.loadFilters();
                        }
                        this.refreshData({ refreshCache: true }).subscribe(() => {
                            this.broadcastRefreshModule(filterSetName);
                        });
                    }
                });
            }
        }, filterSetName);
    }
    linkRecord(record) {
        super.linkRecord(record);
        this.defaultValueSubscription = record.load$
            .subscribe(() => {
            this.hasChanges = false;
        });
        this.recordChangeSubscription = record.allChangesGet$().pipe(debounceTime(15))
            .subscribe(() => {
            this.hasChanges = this.checkChangedData();
        });
    }
    checkChangedData() {
        let hasChanges = false;
        if (this.record) {
            forEachKey(this.record.changedData, (val, key) => {
                if (this.excludeList.indexOf(key) === -1 && key !== this.columnName) {
                    hasChanges = true;
                }
            });
        }
        return hasChanges;
    }
    unlinkRecord(keepState = false) {
        if (this.filterModuleSubscription) {
            this.filterModuleSubscription.unsubscribe();
            this.filterModuleSubscription = undefined;
            if (!keepState) {
                this.clearOptionData();
            }
        }
        if (this.defaultValueSubscription) {
            this.defaultValueSubscription.unsubscribe();
            this.defaultValueSubscription = undefined;
        }
        if (this.recordChangeSubscription) {
            this.recordChangeSubscription.unsubscribe();
            this.recordChangeSubscription = undefined;
        }
        super.unlinkRecord(keepState);
    }
    broadcastRefreshModule(deletedFilterSet = '') {
        DIContainer.getInstance('BroadcastManager')
            .send('refresh_filtermodule', {
            payload: {
                module: this.record.filterModule,
                deletedFilterSet
            },
            initiatorId: this.initiatorId
        });
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        this._removeActionMapping(properties);
        this._removeParamMapping(properties);
        delete properties.columnName;
        delete properties.defaultValue;
        delete properties.useNativeSelect;
        delete properties.multiSelect;
        delete properties.multiSelectDragToSort;
        delete properties.displayColumnName;
        // properties.excludeList = ({
        // 	id: 'excludeList'
        // 	,value: this.excludeList
        // 	,componentType: 'Select'
        // 	,componentProperties: {
        // 		multiSelect: true
        // 		,multiSelectValueType: 'ARRAY'
        // 		,allowFreeFormEntry: true
        // 	}
        // 	,captionKey: 'formbuilder_filtertoggle_excludelist'
        // 	,propertyGroup: 'data'
        // 	,saveFlag: this.excludeList.length > 0
        // })
        return properties;
    }
    destroy() {
        this.iconSubject?.complete();
        this.iconSubject = undefined;
        super.destroy();
    }
}
FilterToggle.VERSION = '$Revision: 111642 $';
